import { useEffect, useRef } from 'react'

import type { TypedDocumentNode } from '@apollo/client'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { debugLog } from '~publish/helpers/debugLog'
import { client } from '~publish/legacy/apollo-client'

export const CONNECTION_REFRESH_DEBOUNCE_MS = 500

type CountedConnectionData = { contentItems: { totalCount: number } }
type PagedVariables = { first: number; after?: string | null }

/**
 * Returns a function that runs `task` once, after a burst of calls settles.
 * A failed task is logged, not rethrown.
 */
export function useDebouncedTask(
  task: () => Promise<unknown>,
  label: string,
): () => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const runTask = useCallbackRef(task)

  useEffect(() => (): void => clearTimeout(timeoutRef.current), [])

  return useCallbackRef((): void => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      runTask().catch((error: unknown) => {
        debugLog(`💽 ${label} failed`, error)
      })
    }, CONNECTION_REFRESH_DEBOUNCE_MS)
  })
}

/**
 * Fetches the connection's `totalCount` from the server and writes it into
 * the cached entry for `variables`, leaving the loaded edges in place.
 */
export async function refreshConnectionTotalCount<
  TData extends CountedConnectionData,
  TVariables extends PagedVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables | undefined,
): Promise<void> {
  if (!variables) return

  const { data } = await client.query({
    query,
    variables: { ...variables, first: 1, after: null },
    fetchPolicy: 'no-cache',
  })
  if (!data) return

  const { totalCount } = data.contentItems
  client.cache.updateQuery({ query, variables }, (existing) =>
    existing
      ? {
          ...existing,
          contentItems: { ...existing.contentItems, totalCount },
        }
      : existing,
  )
}

/**
 * Schedules a `totalCount` refresh when an item missing from the loaded
 * edges may still sit on an unloaded page.
 */
export function refreshCountIfPagesUnloaded(
  connection: { pageInfo: { hasNextPage: boolean } },
  scheduleCountRefresh: () => void,
): void {
  // With every page loaded, a missing item was never in this connection.
  if (connection.pageInfo.hasNextPage) {
    scheduleCountRefresh()
  }
}
